import {
  useState,
  useEffect,
  useCallback,
  useRef,
  Suspense,
  type ReactNode,
} from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { menuItemAnchorKey } from '@/components/walkthrough/anchors';
import {
  LayoutDashboard,
  BookOpen,
  User,
  LogOut,
  X,
  Award,
  ChevronRight,
  ExternalLink,
  Users,
  ChevronDown,
  PanelLeftOpen,
  Pin,
  PinOff,
  Clapperboard,
  ArrowLeft,
  Pencil,
  type LucideIcon,
} from 'lucide-react';
import {
  usePublicMenuItems,
  usePublicGamificationSettings,
  useCourseBySlug,
  useAccessibleMemberAreas,
  useMyPoints,
} from '@/hooks';
import { cn } from '@/lib/utils';
import { courseCatalogUrl } from '@/lib/course-navigation';
import { ecosystemKey, isPlatformAdmin } from '@/lib/member-home';
import { WhatsAppBubble } from '@/components/ui/WhatsAppBubble';
import { GamificationWidget } from '@/components/student/GamificationWidget';
import { SiteBannerCarousel } from '@/components/student/SiteBannerCarousel';
import { GamifiedAvatar } from '@/components/ui/GamifiedAvatar';
import { devflixService } from '@/services/devflix.service';
import { AppToolTip } from '../ui/AppToolTip';
import { useLateralAutomatica } from '@/hooks/useLateralAutomatica';
import { rotaReal } from '@/lib/rotas';
import { resolveSidebarIcon } from '@/lib/sidebar-icons';
import { BarraInferior } from './BarraInferior';
import { CarregandoPagina } from '@/components/ui/CarregandoPagina';
import { useRealceDoMenu } from './useRealceDoMenu';
import { FitaDeLed } from './FitaDeLed';
import { HomeLayout, MemberLayout } from './HomeLayout';
import { Button } from '@/components/ui/button';
import { DevClubLogo } from '@/components/ui/DevClubLogo';

interface StudentLayoutProps {
  /** Renders the layout as an in-place appearance editor. */
  editMode?: boolean;
  /** Editable sidebar nav, rendered in place of the menu when in edit mode. */
  sidebarSlot?: ReactNode;
  /** Main content; falls back to the routed <Outlet/> when omitted. */
  children?: ReactNode;
}

function normalizeStudentMenuUrl(url?: string) {
  if (!url?.startsWith('/') || url.startsWith('//')) return url;
  const [pathname] = url.split(/[?#]/, 1);
  const courseLessons = pathname.match(/^\/cursos\/([^/]+)\/aulas\/?$/);
  if (courseLessons) {
    return `/courses/${courseLessons[1]}/trail${url.slice(pathname.length)}`;
  }
  const routes: Record<string, string> = {
    '/cursos': '/courses',
    '/perfil': '/profile',
    '/certificados': '/certificates',
    '/dashboard': '/home',
  };
  const target = routes[pathname.replace(/\/$/, '')];
  return target ? target + url.slice(pathname.length) : url;
}

export function StudentLayout(props: StudentLayoutProps = {}) {
  const { pathname } = useLocation();

  if (props.editMode) {
    return <MemberLayout><LegacyStudentLayout {...props} /></MemberLayout>;
  }

  if (pathname === '/home') {
    return <HomeLayout>{props.children}</HomeLayout>;
  }

  return <LegacyStudentLayout {...props} />;
}

function LegacyStudentLayout({
  editMode = false,
  sidebarSlot,
  children,
}: StudentLayoutProps = {}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [devflixLoading, setDevflixLoading] = useState(false);
  const [devflixError, setDevflixError] = useState<string | null>(null);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(() => {
    const stored = localStorage.getItem('desktop-sidebar-open');
    return stored === null ? true : stored === 'true';
  });
  /**
   * No desktop o menu começa fixado aberto. O botão do topo permite recolher
   * para a faixa de ícones que expande no hover e salva essa preferência.
   * É separado de `desktopSidebarOpen` (que esconde a barra por completo).
   */
  const { fixada, alternarFixada, sobre, eventos } =
    useLateralAutomatica('sidebar-fixada', true);
  /** Com a barra abrindo no hover, o painel de progresso abre só naquela visita. */
  const [progressoNoHover, setProgressoNoHover] = useState(false);
  /** Bloco de progresso: fechado por padrão, para o menu começar curto. */
  const [progressoAberto, setProgressoAberto] = useState(() => {
    return localStorage.getItem('sidebar-progresso-aberto') === 'true';
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const location = useLocation();
  // o realce da lateral desliza com mola e se arrasta (como as abas do painel)
  const menu = useRealceDoMenu(
    location.pathname,
    // Só o véu verde: a barrinha de 3 px na esquerda seguia o canto arredondado
    // e virava um contorno em volta do item ao clicar.
    'rounded-md bg-primary/10',
  );
  const { user, logout } = useAuthStore();
  const { data: publicGamificationSettings } = usePublicGamificationSettings();

  // When viewing a specific course (/courses/:slug[/...]), a per-course sidebar
  // overrides the global menu. The query key matches CoursePage, so it's shared.
  const courseSlugMatch = location.pathname.match(/^\/courses\/([^/]+)/);
  const activeCourseSlug = courseSlugMatch?.[1];
  const courseOverview = /^\/courses\/[^/]+\/?$/.test(location.pathname) && !editMode;
  const catalogOverview = /^\/courses\/?$/.test(location.pathname) && !editMode;
  const memberAreaOverview = /^\/areas\/[^/]+\/?$/.test(location.pathname) && !editMode;
  const formationOverview = courseOverview || catalogOverview || memberAreaOverview;
  const parentCatalogParams = new URLSearchParams(location.search);
  const insideCatalogSection = catalogOverview && parentCatalogParams.has('section');
  parentCatalogParams.delete('section');
  const parentCatalog = `/courses${parentCatalogParams.size ? `?${parentCatalogParams}` : ''}`;
  const returnUrl = courseOverview ? courseCatalogUrl(location.search)
    : memberAreaOverview ? '/courses'
      : insideCatalogSection ? parentCatalog : '/home';
  const returnLabel = courseOverview || memberAreaOverview ? 'Voltar para formações'
    : insideCatalogSection ? 'Voltar para área' : 'Voltar para o início';
  const { data: activeCourse, isLoading: courseLoading, isError: courseError } = useCourseBySlug(
    activeCourseSlug || '',
    !!activeCourseSlug,
  );
  const courseMenuItems = courseError ? undefined : activeCourse?.menuItems?.filter(item => item.isActive !== false);
  const hasCourseMenu = !!(courseMenuItems && courseMenuItems.length > 0);
  const admin = isPlatformAdmin(user?.role);
  const contextParams = new URLSearchParams(location.search);
  const directAreaId = activeCourse?.memberAreaId || contextParams.get('area') ||
    location.pathname.match(/^\/areas\/([^/]+)/)?.[1];
  const requestedEcosystem = ecosystemKey(contextParams.get('ecosystem') || '');
  const resolveEcosystem = !editMode && !hasCourseMenu && !directAreaId && !!requestedEcosystem;
  const areaQuery = useAccessibleMemberAreas(resolveEcosystem);
  const ecosystemAreas = resolveEcosystem ? areaQuery.data?.filter(area =>
    ecosystemKey(area.slug) === requestedEcosystem &&
    (admin || (area.isActive && area.access?.allowed)),
  ) : undefined;
  const menuAreaId = directAreaId || (ecosystemAreas?.length === 1 ? ecosystemAreas[0].id : undefined);
  const contextLoading = !!(activeCourseSlug && courseLoading) || (resolveEcosystem && areaQuery.isLoading);
  const contextError = !!(activeCourseSlug && courseError) ||
    (resolveEcosystem && (areaQuery.isError || (!areaQuery.isLoading && !ecosystemAreas?.length)));
  const publicMenu = usePublicMenuItems(menuAreaId, !editMode && !hasCourseMenu && !contextLoading && !contextError);
  const customMenuItems = publicMenu.isError ? undefined : publicMenu.data;
  const menuLoading = contextLoading || (!hasCourseMenu && publicMenu.isLoading);
  const menuError = contextError || (!hasCourseMenu && publicMenu.isError);
  const menuEditorUrl = activeCourse?.id
    ? `/admin/menus?course=${encodeURIComponent(activeCourse.id)}`
    : menuAreaId ? `/admin/menus?area=${encodeURIComponent(menuAreaId)}` : '/admin/menus';
  const menuEditorLabel = activeCourse?.id ? 'Editar menu deste curso'
    : menuAreaId ? 'Editar menu desta área' : 'Editar menu geral';

  // Dentro de um curso o menu vira o do curso e a pessoa precisa de um jeito
  // de voltar sem caçar o logo: o "Voltar" leva à última tela do menu
  // principal de onde ela veio (Início, Cursos…), ou ao catálogo.
  const ultimaForaDoCurso = useRef<string>(
    sessionStorage.getItem('devcourse-voltar-do-curso') || '/courses',
  );
  useEffect(() => {
    if (activeCourseSlug || location.pathname.startsWith('/lesson')) return;
    ultimaForaDoCurso.current = location.pathname + location.search;
    try {
      sessionStorage.setItem(
        'devcourse-voltar-do-curso',
        ultimaForaDoCurso.current,
      );
    } catch {
      /* navegação privada: vale só nesta aba */
    }
  }, [activeCourseSlug, location.pathname, location.search]);
  // Unified list the nav renders: per-course menu wins when present.
  const sourceMenuItems = hasCourseMenu ? courseMenuItems : customMenuItems;
  const normalizedMenuItems = sourceMenuItems
    ?.filter(item => item.isActive !== false)
    .filter(item => !['meuscursos', 'minhasformacoes', 'suasformacoes'].includes(item.title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '')))
    .map(item => ({ ...item, url: normalizeStudentMenuUrl(item.url) }));
  const effectiveMenuItems = sourceMenuItems?.length && normalizedMenuItems &&
    !normalizedMenuItems.some(item => item.url?.split(/[?#]/, 1)[0].replace(/\/$/, '') === '/works-ai')
    ? [...normalizedMenuItems, {
        id: 'platform-works-ai', title: 'Works AI', url: '/works-ai', icon: 'Users',
        order: Math.max(-1, ...normalizedMenuItems.map(item => item.order)) + 1,
        isExternal: false, openNewTab: false,
      }]
    : normalizedMenuItems;

  const gamificationEnabled = publicGamificationSettings?.enabled;

  /**
   * Preferência do balão de WhatsApp, por usuário.
   *
   * O interruptor saiu da barra lateral (ocupava três linhas para algo que se
   * ajusta uma vez), mas a preferência continua valendo — quem já desligou
   * antes segue com ele desligado. Quem alterna hoje é a tela de Configurações.
   */
  const bubbleKey = user?.id ? `whatsapp-bubble-${user.id}` : null;
  const [showBubble] = useState(() => {
    if (!bubbleKey) return true;
    const stored = localStorage.getItem(bubbleKey);
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // A gaveta do celular fecha ao trocar de página (pela barra de baixo ou por
  // um item do menu), senão ela fica aberta por cima da tela nova.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const toggleDesktopSidebar = useCallback(() => {
    setDesktopSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem('desktop-sidebar-open', String(next));
      return next;
    });
  }, []);

  /** No mobile a barra é uma gaveta: sempre completa quando aberta. */
  const recolhida = !fixada && !isMobile;
  const aberta = !recolhida || sobre;
  const progressoVisivel =
    aberta && (recolhida ? progressoNoHover : progressoAberto);

  const alternarProgresso = useCallback(() => {
    if (recolhida) {
      setProgressoNoHover((v) => !v);
      return;
    }
    setProgressoAberto((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar-progresso-aberto', String(next));
      return next;
    });
  }, [recolhida]);

  useEffect(() => {
    if (!sobre) setProgressoNoHover(false);
  }, [sobre]);

  const openDevflix = useCallback(() => {
    setDevflixLoading(true);
    setDevflixError(null);
    try {
      devflixService.open();
    } catch {
      setDevflixError('Não foi possível abrir a DevFlix agora.');
      setDevflixLoading(false);
    }
  }, []);

  // Nível e XP no cabeçalho do bloco: é a informação que a pessoa quer de
  // relance, sem precisar abrir o painel inteiro.
  const { data: meusPontos } = useMyPoints();
  const xpTotal = meusPontos?.totalPoints ?? 0;
  const nivelAtual = meusPontos?.level;

  return (
    <div className="student-shell bg-background min-h-screen overflow-x-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className=" fixed inset-0 z-40 bg-black/60  lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-tour="sidebar"
        data-lateral=""
        data-aberta={aberta}
        {...eventos}
        className={cn(
          'ease-smooth fixed left-0 top-0 z-50 transform transition-all duration-300',
          // No celular a gaveta para acima da barra de baixo, que continua à mão.
          editMode
            ? 'bottom-0'
            : 'bottom-[calc(4rem+env(safe-area-inset-bottom))] lg:bottom-0',
          'bg-card/95 border-border/50 flex flex-col border-r backdrop-blur-xl',
          // Gaveta no mobile mantém a largura cheia; no desktop, 240px aberta
          // ou 72px de faixa, só com os ícones.
          'w-72 overflow-hidden',
          aberta ? 'lg:w-60' : 'lg:w-[72px]',
          // aberta por cima do conteúdo (no hover), ganha sombra para descolar
          recolhida && sobre && 'lg:shadow-2xl lg:shadow-black/50',
          // Mobile: controlled by sidebarOpen
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: controlled by desktopSidebarOpen
          desktopSidebarOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full',
        )}
      >
        {/* Fita de LED do topo: passa quando o logo roda código e quando a barra abre */}
        <FitaDeLed aberta={isMobile ? sidebarOpen : aberta} />

        {/* Largura fixa por dentro: a barra cresce e os textos já estão no
            lugar, sem quebrar linha enquanto ela abre. */}
        <div className="flex h-full w-full flex-col lg:w-60 lg:shrink-0">
          {/* Sidebar Header: sem linha embaixo, porque a do header da página
              fica em outra altura e as duas não casavam. */}
          <div className="flex h-16 shrink-0 items-center justify-between pl-[22px] pr-3">
            <Link
              to="/home"
              data-tour="sidebar-logo"
              className="rotulo-logo group flex items-center gap-3"
            >
              <DevClubLogo size="sm" showText vivo />
            </Link>
            {/* Mobile close */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label="Fechar menu"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
            {/* Desktop: fixa a barra aberta, ou deixa ela abrir sozinha no hover. */}
            <AppToolTip
              content={
                fixada ? 'Deixar o menu abrir sozinho' : 'Fixar o menu aberto'
              }
            >
              <Button
                variant="ghost"
                size="icon-sm"
                className="rotulo hidden lg:inline-flex"
                onClick={alternarFixada}
                aria-pressed={fixada}
                aria-label={
                  fixada ? 'Deixar o menu abrir sozinho' : 'Fixar o menu aberto'
                }
              >
                {fixada ? (
                  <PinOff className="text-muted-foreground hover:text-foreground h-4 w-4 transition-colors" />
                ) : (
                  <Pin className="text-muted-foreground hover:text-foreground h-4 w-4 transition-colors" />
                )}
              </Button>
            </AppToolTip>
          </div>

          {/*
          Progresso do aluno, recolhido por padrão.
          O nome e o e-mail saíram: são a informação que a pessoa menos precisa
          reler (ela já sabe quem é), e ocupavam a faixa mais nobre da barra. A
          identificação continua no avatar e no menu da conta, no topo.
        */}
          {gamificationEnabled && (
            <div
              data-tour="sidebar-progress"
              className="border-border/50 shrink-0 border-b"
            >
              <button
                onClick={alternarProgresso}
                aria-expanded={progressoVisivel}
                className="hover:bg-secondary/40 flex w-full items-center gap-2.5 py-2.5 pl-[18px] pr-3 transition-colors"
              >
                <GamifiedAvatar
                  src={user?.avatar}
                  name={user?.name}
                  size="sm"
                  showLevelBadge
                />
                <span className="rotulo min-w-0 flex-1 text-left">
                  <span className="text-foreground block truncate text-sm font-medium">
                    {nivelAtual?.name ?? 'Estagiário'}
                  </span>
                  <span className="text-muted-foreground block font-mono text-[11px] tabular-nums">
                    {xpTotal.toLocaleString('pt-BR')} XP
                  </span>
                </span>
                <ChevronDown
                  className={cn(
                    'rotulo text-muted-foreground h-4 w-4 transition-transform duration-300',
                    progressoVisivel && 'rotate-180',
                  )}
                />
              </button>
              {/*
              O widget repete nível e ícone no próprio cabeçalho; aqui isso já
              está no botão acima, então escondemos a primeira linha dele e
              sobra o que interessa: barra de progresso, quanto falta e ranking.
              Abre deslizando e aparecendo, em vez de surgir de uma vez.
            */}
              <div
                aria-hidden={!progressoVisivel}
                className={cn(
                  'grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(.22,1,.36,1)]',
                  progressoVisivel ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
              >
                <div className="min-h-0 overflow-hidden">
                  <div
                    className={cn(
                      'progresso-sem-cabecalho origin-top transition-[opacity,transform] duration-300 ease-out',
                      progressoVisivel
                        ? 'translate-y-0 scale-100 opacity-100 delay-75'
                        : 'invisible -translate-y-1 scale-[0.97] opacity-0',
                    )}
                  >
                    <GamificationWidget compact={isMobile} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation - scrollable area */}
          <nav
            ref={menu.navRef}
            {...menu.navProps}
            data-tour="sidebar-nav"
            className={cn(
              'relative flex-1 select-none space-y-0.5 overflow-y-auto overflow-x-hidden px-2 py-3',
              menu.arrastando && 'cursor-grabbing',
            )}
          >
            {menu.realce}
            {hasCourseMenu && !editMode && (
              <Link
                to={ultimaForaDoCurso.current}
                draggable={false}
                className="nav-item text-muted-foreground hover:text-foreground group mb-2"
              >
                <div className="bg-secondary/50 group-hover:bg-primary/10 rounded-lg p-2 transition-colors duration-200">
                  <ArrowLeft className="group-hover:text-primary h-4 w-4 transition-[color,transform] duration-200 group-hover:-translate-x-0.5" />
                </div>
                <span className="rotulo flex-1 truncate font-medium">
                  Voltar ao menu
                </span>
              </Link>
            )}
            <div className="mb-2 flex items-center justify-between px-3">
              <p className="rotulo text-muted-foreground text-[11px] font-semibold uppercase tracking-wider">
                {hasCourseMenu ? 'Neste curso' : menuAreaId ? 'Nesta área' : 'Menu'}
              </p>
              {admin && !editMode && !contextLoading && !contextError && (
                <Link
                  to={menuEditorUrl}
                  aria-label={menuEditorLabel}
                  title={menuEditorLabel}
                  className="text-muted-foreground hover:bg-secondary hover:text-foreground flex h-11 w-11 shrink-0 items-center justify-center rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </Link>
              )}
            </div>

            {/* Dynamic Menu Items from Database */}
            {editMode && sidebarSlot ? (
              sidebarSlot
            ) : menuError ? (
              <div role="alert" className="text-muted-foreground space-y-2 px-3 py-2 text-sm">
                <p>Não foi possível carregar o menu desta página.</p>
                <Link to="/home" className="text-primary underline">Voltar para o início</Link>
              </div>
            ) : menuLoading ? (
              /* Skeleton while loading — prevents flash of fallback menu */
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5"
                >
                  <div className="bg-secondary/50 h-8 w-8 animate-pulse rounded-lg" />
                  <div className="bg-secondary/50 h-4 w-24 animate-pulse rounded" />
                </div>
              ))
            ) : effectiveMenuItems &&
              Array.isArray(effectiveMenuItems) &&
              effectiveMenuItems.length > 0 ? (
              [...effectiveMenuItems]
                .sort((a, b) => a.order - b.order)
                .map((item, index) => {
                  const IconComponent = resolveSidebarIcon(item.icon);
                  const isExt = item.isExternal || item.openNewTab;

                  if (isExt) {
                    return (
                      <a
                        key={item.id}
                        href={item.url || '#'}
                        target={item.openNewTab ? '_blank' : undefined}
                        rel={
                          item.openNewTab ? 'noopener noreferrer' : undefined
                        }
                        // também é parada do arraste do realce (abre numa aba)
                        data-item-menu={item.url || undefined}
                        data-externo=""
                        draggable={false}
                        style={{ animationDelay: `${index * 50}ms` }}
                        className={cn(
                          'nav-item animate-fade-in group',
                          menu.aceso === item.url
                            ? 'active'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        <div
                          className={cn(
                            'rounded-lg p-2 transition-all duration-200',
                            menu.aceso === item.url
                              ? 'bg-primary/20'
                              : 'bg-secondary/50 group-hover:bg-primary/10',
                          )}
                        >
                          <IconComponent
                            className={cn(
                              'h-4 w-4 transition-colors',
                              menu.aceso === item.url
                                ? 'text-primary'
                                : 'group-hover:text-primary',
                            )}
                          />
                        </div>
                        <span className="rotulo flex-1 truncate font-medium">
                          {item.title}
                        </span>
                        <ExternalLink className="text-muted-foreground h-3 w-3 opacity-50" />
                      </a>
                    );
                  }

                  return (
                    <Link
                      key={item.id}
                      to={rotaReal(item.url) || '#'}
                      data-item-menu={rotaReal(item.url)}
                      draggable={false}
                      data-tour={menuItemAnchorKey(item.title)}
                      style={{ animationDelay: `${index * 50}ms` }}
                      className={cn(
                        'nav-item animate-fade-in group',
                        menu.aceso === rotaReal(item.url)
                          ? 'active'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <div
                        className={cn(
                          'rounded-lg p-2 transition-all duration-200',
                          menu.aceso === rotaReal(item.url)
                            ? 'bg-primary/20'
                            : 'bg-secondary/50 group-hover:bg-primary/10',
                        )}
                      >
                        <IconComponent
                          className={cn(
                            'h-4 w-4 transition-colors',
                            menu.aceso === rotaReal(item.url)
                              ? 'text-primary'
                              : 'group-hover:text-primary',
                          )}
                        />
                      </div>
                      <span className="rotulo flex-1 truncate font-medium">
                        {item.title}
                      </span>
                      {menu.aceso === rotaReal(item.url) && (
                        <ChevronRight className="text-primary h-4 w-4" />
                      )}
                    </Link>
                  );
                })
            ) : (
              <>
                {[
                  { title: 'Home', url: '/home', Icon: LayoutDashboard },
                  { title: 'Cursos', url: '/courses', Icon: BookOpen },
                  ...(gamificationEnabled
                    ? [{ title: 'Ranking', url: '/ranking', Icon: Award }]
                    : []),
                  { title: 'Comunidade', url: '/community', Icon: Users },
                  { title: 'Works AI', url: '/works-ai', Icon: Users },
                  { title: 'Certificados', url: '/certificates', Icon: Award },
                  { title: 'Perfil', url: '/profile', Icon: User },
                ].map(
                  (
                    item: {
                      title: string;
                      url: string;
                      Icon: LucideIcon;
                      disabled?: boolean;
                    },
                    index,
                  ) =>
                    item.disabled ? (
                      <div
                        key={item.url}
                        data-tour={menuItemAnchorKey(item.title)}
                        style={{ animationDelay: `${index * 50}ms` }}
                        className="nav-item animate-fade-in text-muted-foreground pointer-events-none w-full opacity-50"
                      >
                        <div className="bg-secondary/50 rounded-lg p-2">
                          <item.Icon className="h-4 w-4" />
                        </div>
                        <span className="rotulo flex-1 truncate font-medium">
                          {item.title}
                        </span>
                        <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase">
                          Em breve
                        </span>
                      </div>
                    ) : (
                      <Link
                        key={item.url}
                        to={item.url}
                        data-item-menu={item.url}
                        draggable={false}
                        data-tour={menuItemAnchorKey(item.title)}
                        style={{ animationDelay: `${index * 50}ms` }}
                        className={cn(
                          'nav-item animate-fade-in group',
                          menu.aceso === rotaReal(item.url)
                            ? 'active'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        <div
                          className={cn(
                            'rounded-lg p-2 transition-all duration-200',
                            menu.aceso === rotaReal(item.url)
                              ? 'bg-primary/20'
                              : 'bg-secondary/50 group-hover:bg-primary/10',
                          )}
                        >
                          <item.Icon
                            className={cn(
                              'h-4 w-4 transition-colors',
                              menu.aceso === rotaReal(item.url)
                                ? 'text-primary'
                                : 'group-hover:text-primary',
                            )}
                          />
                        </div>
                        <span className="rotulo flex-1 truncate font-medium">
                          {item.title}
                        </span>
                        {menu.aceso === rotaReal(item.url) && (
                          <ChevronRight className="text-primary h-4 w-4" />
                        )}
                      </Link>
                    ),
                )}
              </>
            )}

            {!editMode && (
              <div className="border-border/50 mt-3 border-t pt-3">
                <button
                  type="button"
                  data-tour="sidebar-devflix"
                  className="nav-item text-muted-foreground hover:text-foreground group w-full disabled:cursor-wait disabled:opacity-70"
                  disabled={devflixLoading}
                  onClick={openDevflix}
                >
                  <div className="bg-primary/10 group-hover:bg-primary/20 rounded-lg p-2 transition-all duration-200">
                    <Clapperboard className="text-primary h-4 w-4" />
                  </div>
                  <span className="rotulo flex-1 truncate text-left font-medium">
                    {devflixLoading ? 'Abrindo DevFlix…' : 'Cursos gratuitos'}
                  </span>
                  <span className="rotulo bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase">
                    DevFlix
                  </span>
                </button>
                {devflixError && aberta && (
                  <p
                    className="text-destructive px-3 pt-2 text-xs"
                    role="alert"
                  >
                    {devflixError}
                  </p>
                )}
              </div>
            )}

            {/*
            Banner da lateral dentro da área que rola, depois do menu. Fixo
            embaixo, ele tapava os últimos itens em telas de 768 px de altura
            ("Central de ajuda" ficava pela metade). Não cabe no recolhido.
          */}
            <div
              className={cn(
                'w-full min-w-0 overflow-hidden pt-3 transition-opacity duration-300',
                aberta ? 'opacity-100' : 'invisible opacity-0',
              )}
            >
              <SiteBannerCarousel
                position="sidebar"
                compact
                autoPlayInterval={7000}
              />
            </div>
          </nav>

          {/*
          Rodapé enxuto: só o sair.
          O interruptor do WhatsApp saiu daqui — ocupava três linhas na barra
          para uma preferência que se mexe uma vez. Ele continua disponível em
          Configurações; o que se controlava aqui é o mesmo estado.
        */}
          <div className="border-border/50 bg-card/80 shrink-0 space-y-0.5 border-t p-2 backdrop-blur-sm">
            <button
              onClick={logout}
              data-tour="sidebar-logout"
              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex min-h-11 w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors lg:min-h-0 lg:px-5"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className="rotulo">Sair</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div
        className={cn(
          'flex min-h-[calc(100dvh-var(--member-header-offset,0px))] flex-col overflow-x-hidden transition-all duration-300',
          // Acompanha a largura da barra, senão o conteúdo fica por baixo dela.
          desktopSidebarOpen
            ? recolhida
              ? 'lg:pl-[72px]'
              : 'lg:pl-60'
            : 'lg:pl-0',
        )}
      >
        {/*
          O banner de topo (a oferta) saiu daqui: em toda página ele ocupava a
          primeira tela, e curso, certificados, perfil e agenda abriam iguais,
          com a oferta no lugar do conteúdo. Ele aparece uma vez, no começo do
          Início, junto com os banners do Início (HomePage).
        */}

        {/* Page content */}
        <main
          id="member-main"
          tabIndex={-1}
          data-tour="main-content"
          className={cn(
            'flex-1 p-3 sm:p-4 md:p-5 lg:p-6',
            formationOverview && '!pt-2',
            // Respiro para a barra fixa de baixo (64 px + área segura do iPhone).
            !editMode &&
              'pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-6',
          )}
        >
          {(formationOverview || (!isMobile && !desktopSidebarOpen)) && (
            <nav aria-label="Navegação da página" className="mb-3 flex min-h-11 items-center gap-3">
              {!desktopSidebarOpen && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Abrir menu"
                  aria-expanded={desktopSidebarOpen}
                  className="hidden lg:inline-flex"
                  onClick={toggleDesktopSidebar}
                >
                  <PanelLeftOpen className="h-5 w-5" aria-hidden="true" />
                </Button>
              )}
              {formationOverview && (
                <Link to={returnUrl} aria-label={returnLabel} className="text-muted-foreground hover:bg-secondary/80 hover:text-foreground inline-flex min-h-11 items-center gap-2 rounded-md px-2 text-sm transition-colors">
                  <ArrowLeft size={16} aria-hidden="true" />
                  <span>{courseOverview || memberAreaOverview ? 'Todas as formações' : insideCatalogSection ? 'Voltar para área' : 'Voltar para o início'}</span>
                </Link>
              )}
            </nav>
          )}
          <div className="animate-fade-in">
            <Suspense fallback={<CarregandoPagina />}>
              {children ?? <Outlet />}
            </Suspense>
          </div>
        </main>
      </div>

      {!editMode && (
        <BarraInferior
          gamificacao={!!gamificationEnabled}
          menuAberto={sidebarOpen}
          onAbrirMenu={() => setSidebarOpen((aberto) => !aberto)}
          onFecharMenu={() => setSidebarOpen(false)}
        />
      )}

      {/* WhatsApp Bubble */}
      {showBubble && !editMode && <WhatsAppBubble />}
    </div>
  );
}
